import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHeader } from "@/components/ui-kit";

export default function ApplicationForm({ overline, title, sub, endpoint, fields, note, consentLabel, submitLabel = "Submit Application" }) {
  const init = Object.fromEntries(fields.map((f) => [f.name, f.type === "checkbox" ? false : ""]));
  const [f, setF] = useState({ ...init, consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (consentLabel && !f.consent) return toast.error("Please confirm to continue");
    setLoading(true);
    try { await api.post(endpoint, f); toast.success("Application received — check your email for confirmation."); setF({ ...init, consent: false }); }
    catch { toast.error("Submission failed. Please try again."); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <SectionHeader overline={overline} title={title} sub={sub} />
      {note && <div className="gw-card p-5 mb-8 flex gap-3" data-testid="privacy-note"><ShieldCheck className="w-6 h-6 text-[#4A5D23] shrink-0" /><p className="text-sm text-[#C8C4B7]">{note}</p></div>}
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        {fields.map((fl) => (
          <div key={fl.name} className={fl.half ? "" : "sm:col-span-2"}>
            {fl.type === "checkbox" ? (
              <label className="flex items-start gap-3 cursor-pointer text-sm text-[#C8C4B7]" data-testid={`field-${fl.name}`} onClick={() => set(fl.name, !f[fl.name])}>
                <span className={`w-5 h-5 border shrink-0 flex items-center justify-center mt-0.5 ${f[fl.name] ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/30"}`}>{f[fl.name] && <span className="text-black text-xs">✓</span>}</span>{fl.label}
              </label>
            ) : (<>
              <label className="block text-sm text-[#C8C4B7] mb-2">{fl.label}{fl.required && <span className="text-[#D4AF37]"> *</span>}</label>
              {fl.type === "textarea" ? (
                <textarea rows={4} required={fl.required} className="gw-input" data-testid={`field-${fl.name}`} value={f[fl.name]} onChange={(e) => set(fl.name, e.target.value)} placeholder={fl.placeholder} />
              ) : fl.type === "select" ? (
                <select required={fl.required} className="gw-input" data-testid={`field-${fl.name}`} value={f[fl.name]} onChange={(e) => set(fl.name, e.target.value)}>
                  <option value="">Select…</option>{fl.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={fl.type || "text"} required={fl.required} className="gw-input" data-testid={`field-${fl.name}`} value={f[fl.name]} onChange={(e) => set(fl.name, e.target.value)} placeholder={fl.placeholder} />
              )}
            </>)}
          </div>
        ))}
        {consentLabel && (
          <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer text-sm text-[#C8C4B7]" data-testid="field-consent" onClick={() => set("consent", !f.consent)}>
            <span className={`w-5 h-5 border shrink-0 flex items-center justify-center mt-0.5 ${f.consent ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/30"}`}>{f.consent && <span className="text-black text-xs">✓</span>}</span>{consentLabel}
          </label>
        )}
        <button disabled={loading} className="btn-gold sm:col-span-2 flex items-center justify-center gap-2 py-3.5" data-testid="application-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} {submitLabel}</button>
      </form>
    </div>
  );
}
